import React, { useEffect, useRef } from 'react';
import { isEmpty, isEqual } from 'lodash';

import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import { getLobDescription } from '~/Utils/lobUtils';

import useLobOptions from './hooks/useLobOptions';

/** Sentinel option that clears the filter. The parent maps it back to an empty selection. */
export const LOB_FILTER_ALL_OPTION_VALUE = 'All';

interface LobFilterMultiSelectProps {
  value: string[];
  onChange: (lobs: string[]) => void;
  subOrganizationIds?: (number | string)[];
  label?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  allOptionValue?: string;
  fullWidth?: boolean;
  error?: boolean;
  /**
   * Notified with the offered LOB set whenever it settles to a new value. This component owns the
   * options fetch; callers that need the set (e.g. to prune a stale selection) subscribe here rather
   * than running a second `useLobOptions`, which would double the GET per render of the pair.
   */
  onOptionsChange?: (lobOptions: string[]) => void;
}

/**
 * A Line of Business multiselect with an "All" sentinel row. The claims-list filter is the canonical
 * usage; callers with different semantics (e.g. the claim-flag scope dialog) supply their own `id`,
 * `allOptionValue` and value mapping.
 *
 * Controlled on purpose: "Reset filters" clears the parent's state, and a field holding its own
 * selection would drop the request param while still showing a selected chip. The disable rule is
 * LobMultiSelect's — no sub-org means no meaningful option set — except that an organization with
 * sub-organizations switched off has no sub-org selector, so it stays enabled on the org-wide set.
 */
const LobFilterMultiSelect: React.FC<LobFilterMultiSelectProps> = ({
  value,
  onChange,
  subOrganizationIds = [],
  label = 'Line of Business',
  className,
  disabled = false,
  id = 'claim_lobs',
  allOptionValue = LOB_FILTER_ALL_OPTION_VALUE,
  fullWidth = false,
  error = false,
  onOptionsChange,
}) => {
  // useOrganization() is untyped JS and resolves to `{}`, so annotate the one field read here.
  const { subOrganizationEnabled }: { subOrganizationEnabled?: boolean } = useOrganization();
  const { lobOptions, isLoading } = useLobOptions({ subOrganizationIds });
  const { lobConfigurationsDict } = useLobConfiguration();

  // Ref'd so an inline arrow from the caller doesn't re-fire the effect (the idiom useLobOptions
  // already uses for fetchLobs/filterLobsFunc). Fires only when the offered set settles to a new
  // value: mid-fetch and non-array states are skipped — useLobOptions can briefly yield undefined,
  // and notifying that would let a subscriber prune against an empty universe.
  const onOptionsChangeRef = useRef(onOptionsChange);
  onOptionsChangeRef.current = onOptionsChange;
  const lastNotifiedOptions = useRef<string[] | null>(null);
  useEffect(() => {
    if (isLoading || !Array.isArray(lobOptions)) {
      return;
    }
    if (isEqual(lastNotifiedOptions.current, lobOptions)) {
      return;
    }
    lastNotifiedOptions.current = lobOptions;
    onOptionsChangeRef.current?.(lobOptions);
  }, [lobOptions, isLoading]);

  const isDisabled = disabled || isLoading || (subOrganizationEnabled && isEmpty(subOrganizationIds));

  // getLobDescription returns undefined for an unconfigured LOB (and for the All sentinel), which
  // would render as "undefined".
  const getLabel = (lob: string): string => getLobDescription(lob, lobConfigurationsDict) || lob;

  return (
    <MultiSelectField<string, string>
      id={id}
      label={label}
      className={className}
      fullWidth={fullWidth}
      error={error}
      value={value}
      onChange={onChange}
      options={lobOptions}
      renderOption={getLabel}
      renderValue={(selectedLobs) => selectedLobs.map(getLabel).join(', ')}
      optionsLoading={isLoading}
      disabled={isDisabled}
      addAllOption
      allOptionValue={allOptionValue}
    />
  );
};

export default LobFilterMultiSelect;
